/**
 * Camino Theme Preset for PrimeNG 21
 *
 * Complete conversion from custom Apollo theme (v17) SCSS to PrimeNG 21 design tokens.
 * Source: packages/core/theme/apollo/src/lib/styles/theme/
 *
 * Light theme (theme-light/indigo): Primary #4547a9, white surfaces
 * Dark theme (theme-dim/indigo): Primary #8871e6, purple surfaces (#242247, #393760)
 *
 * This preset preserves all custom:
 * - Colors, shadows, borders from _variables.scss
 * - Component-specific styling from theme-base/components/
 * - Extensions (stepper, flexmonster, float_label, inputgroup, textarea)
 */

import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

export const CaminoPreset = definePreset(Aura, {
  semantic: {
    // Primary color palette - Indigo
    primary: {
      50: '#f6f7ff',
      100: '#d3d8fe', // Used for focus rings in light mode
      200: '#bec2ff', // primaryLightColor in light theme
      300: '#8c9bfd',
      400: '#697dfc',
      500: '#4547a9', // Light theme primary ($primaryColor)
      600: '#3a3c83', // primaryDarkColor in light theme
      700: '#3142b0',
      800: '#2f3066', // primaryDarkerColor in light theme
      900: '#1c2664',
      950: '#121640',
    },

    colorScheme: {
      // ===== LIGHT THEME =====
      // Source: theme-light/_variables.scss + theme-light/indigo/theme.scss
      light: {
        primary: {
          color: '#4547a9', // $primaryColor
          contrastColor: '#ffffff', // $primaryTextColor
          hoverColor: '#3a3c83', // $primaryDarkColor
          activeColor: '#2f3066', // $primaryDarkerColor
        },
        highlight: {
          background: '#eef2ff', // $highlightBg
          focusBackground: 'rgba(69, 71, 169, 0.24)', // $highlightFocusBg
          color: '#2f3066', // $highlightTextColor ($primaryDarkerColor)
          focusColor: '#2f3066',
        },
        surface: {
          // Shade palette: $shade000 through $shade900
          0: '#ffffff', // $shade000 - surface
          50: '#f9fafb', // $shade100 - header background
          100: '#f3f4f6', // $shade200 - hover background
          200: '#e5e7eb', // $shade300 - border, divider
          300: '#d1d5db', // $shade400 - input border
          400: '#9ca3af', // $shade500 - input icon
          500: '#6b7280', // $shade600 - text secondary color
          600: '#4b5563', // $shade700 - text color
          700: '#374151', // $shade800
          800: '#1f2937', // $shade900
          900: '#111827',
          950: '#030712',
        },
        content: {
          background: '#ffffff', // $inputBg, $panelContentBg
          hoverBackground: '#f9fafb', // $shade100
          borderColor: '#e5e7eb', // $shade300, $divider
          color: '#4b5563', // $textColor ($shade700)
          hoverColor: '#4b5563',
        },
        text: {
          color: '#4b5563', // $textColor
          hoverColor: '#4b5563',
          mutedColor: '#6b7280', // $textSecondaryColor ($shade600)
          hoverMutedColor: '#6b7280',
        },
      },

      // ===== DARK THEME =====
      // Source: theme-dim/_variables.scss + theme-dim/indigo/theme.scss
      dark: {
        primary: {
          color: '#8871e6', // $primaryColor
          contrastColor: '#030712', // $primaryTextColor
          hoverColor: '#9788d3', // $primaryLightColor
          activeColor: '#c7d2fe', // $primaryLighterColor
        },
        highlight: {
          background: 'rgba(129, 140, 248, 0.16)', // $highlightBg
          focusBackground: 'rgba(136, 113, 230, 0.24)', // $highlightFocusBg (primary)
          color: 'rgba(255, 255, 255, 0.87)', // $highlightTextColor
          focusColor: 'rgba(255, 255, 255, 0.87)',
        },
        surface: {
          // Custom dark theme surface colors
          0: '#242247', // $shade700 - ground surface / menu bg
          50: '#2e2c54', // Body bg (--surface-ground)
          100: '#333156', // Input backgrounds (slightly darker than cards)
          200: '#393760', // Card/overlay backgrounds
          300: '#4b5563',
          400: '#6b7280',
          500: '#9ca3af',
          600: '#d1d5db',
          700: '#f3f4f6',
          800: '#f9fafb',
          900: '#ffffff',
          950: '#ffffff',
        },
        content: {
          background: '#393760', // $shade800, $inputBg, $panelContentBg
          hoverBackground: 'rgba(255, 255, 255, 0.03)', // $hoverBg
          borderColor: 'rgba(0, 0, 0, 0.3)', // $shade600, $divider
          color: 'rgba(255, 255, 255, 0.87)', // $textColor ($shade000)
          hoverColor: 'rgba(255, 255, 255, 0.87)',
        },
        text: {
          color: 'rgba(255, 255, 255, 0.87)', // $textColor
          hoverColor: 'rgba(255, 255, 255, 0.87)',
          mutedColor: 'rgba(255, 255, 255, 0.6)', // $textSecondaryColor ($shade100)
          hoverMutedColor: 'rgba(255, 255, 255, 0.6)',
        },
      },
    },

    // Global design tokens
    formField: {
      paddingX: '0.75rem', // $inputPadding: 0.75rem 0.75rem
      paddingY: '0.75rem',
      borderRadius: '6px', // $borderRadius
      focusRing: {
        width: '0', // $focusOutline: 0 none
        style: 'none',
        color: 'transparent',
        offset: '0', // $focusOutlineOffset
        shadow: '0 0 0 1px {primary.500}', // $focusShadow (uses $focusOutlineColor/$primaryLightColor)
      },
      transitionDuration: '0.2s', // $transitionDuration
    },

    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
    },

    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px', // $borderRadius
      lg: '8px',
      xl: '12px',
    },

    transitionDuration: '0.2s', // $transitionDuration
  },

  components: {
    // ===== BUTTON COMPONENT =====
    // Source: theme-base/components/button/_button.scss
    button: {
      colorScheme: {
        light: {
          root: {
            // Primary button ($buttonBg, $buttonTextColor, etc.)
            primary: {
              background: '{primary.500}',
              hoverBackground: '{primary.600}', // $buttonHoverBg: $primaryDarkColor
              activeBackground: '{primary.800}', // $buttonActiveBg: $primaryDarkerColor
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.600}',
              activeBorderColor: '{primary.800}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '{primary.100}',
                shadow: '0 0 0 0.2rem {primary.100}',
              },
            },
            // Secondary button ($secondaryButtonBg, etc.)
            secondary: {
              background: '#64748b',
              hoverBackground: '#475569',
              activeBackground: '#334155',
              borderColor: '#64748b',
              hoverBorderColor: '#475569',
              activeBorderColor: '#334155',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#e2e8f0',
                shadow: '0 0 0 0.2rem #e2e8f0',
              },
            },
            success: {
              background: '{green.500}',
              hoverBackground: '#16a34a',
              activeBackground: '#15803d',
              borderColor: '{green.500}',
              hoverBorderColor: '#16a34a',
              activeBorderColor: '#15803d',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#bbf7d0',
                shadow: '0 0 0 0.2rem #bbf7d0',
              },
            },
            info: {
              background: '#3b82f6',
              hoverBackground: '#2563eb',
              activeBackground: '#1d4ed8',
              borderColor: '#3b82f6',
              hoverBorderColor: '#2563eb',
              activeBorderColor: '#1d4ed8',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#bfdbfe',
                shadow: '0 0 0 0.2rem #bfdbfe',
              },
            },
            warn: {
              background: '#f59e0b',
              hoverBackground: '#d97706',
              activeBackground: '#b45309',
              borderColor: '#f59e0b',
              hoverBorderColor: '#d97706',
              activeBorderColor: '#b45309',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#fde68a',
                shadow: '0 0 0 0.2rem #fde68a',
              },
            },
            help: {
              background: '#a855f7',
              hoverBackground: '#9333ea',
              activeBackground: '#7e22ce',
              borderColor: '#a855f7',
              hoverBorderColor: '#9333ea',
              activeBorderColor: '#7e22ce',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#e9d5ff',
                shadow: '0 0 0 0.2rem #e9d5ff',
              },
            },
            danger: {
              background: '#ef4444',
              hoverBackground: '#dc2626',
              activeBackground: '#b91c1c',
              borderColor: '#ef4444',
              hoverBorderColor: '#dc2626',
              activeBorderColor: '#b91c1c',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '#fecaca',
                shadow: '0 0 0 0.2rem #fecaca',
              },
            },
            contrast: {
              background: '{surface.900}',
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              borderColor: '{surface.900}',
              color: '{surface.0}',
            },
          },
          outlined: {
            // $textButtonHoverBgOpacity: 0.04, $textButtonActiveBgOpacity: 0.16
            primary: {
              hoverBackground: 'rgba(69, 71, 169, 0.04)',
              activeBackground: 'rgba(69, 71, 169, 0.16)',
              borderColor: '{primary.500}',
              color: '{primary.500}',
            },
            secondary: {
              hoverBackground: 'rgba(100, 116, 139, 0.04)',
              activeBackground: 'rgba(100, 116, 139, 0.16)',
              borderColor: '#64748b',
              color: '#64748b',
            },
            success: {
              hoverBackground: 'rgba(77, 217, 100, 0.04)',
              activeBackground: 'rgba(77, 217, 100, 0.16)',
              borderColor: '{green.500}',
              color: '{green.500}',
            },
            info: {
              hoverBackground: 'rgba(59, 130, 246, 0.04)',
              activeBackground: 'rgba(59, 130, 246, 0.16)',
              borderColor: '#3b82f6',
              color: '#3b82f6',
            },
            warn: {
              hoverBackground: 'rgba(245, 158, 11, 0.04)',
              activeBackground: 'rgba(245, 158, 11, 0.16)',
              borderColor: '#f59e0b',
              color: '#f59e0b',
            },
            help: {
              hoverBackground: 'rgba(168, 85, 247, 0.04)',
              activeBackground: 'rgba(168, 85, 247, 0.16)',
              borderColor: '#a855f7',
              color: '#a855f7',
            },
            danger: {
              hoverBackground: 'rgba(239, 68, 68, 0.04)',
              activeBackground: 'rgba(239, 68, 68, 0.16)',
              borderColor: '#ef4444',
              color: '#ef4444',
            },
          },
          text: {
            primary: {
              hoverBackground: 'rgba(69, 71, 169, 0.04)',
              activeBackground: 'rgba(69, 71, 169, 0.16)',
              color: '{primary.500}',
            },
            // $plainButtonTextColor: $textSecondaryColor
            plain: {
              hoverBackground: '{surface.200}',
              activeBackground: '{surface.300}',
              color: '{text.mutedColor}',
            },
          },
        },
        dark: {
          root: {
            primary: {
              background: '{primary.color}',
              hoverBackground: '{primary.hoverColor}',
              activeBackground: '{primary.activeColor}',
              borderColor: '{primary.color}',
              color: '{primary.contrastColor}',
            },
            // Dark theme buttons use lighter colors with dark text
            secondary: {
              background: '#cbd5e1',
              hoverBackground: '#b4bfcc',
              activeBackground: '#9ca9b8',
              borderColor: '#cbd5e1',
              color: '#1c2127',
            },
            success: {
              background: '#0bbf2a',
              hoverBackground: '#0aa625',
              activeBackground: '#098d20',
              borderColor: '#0bbf2a',
              color: '#1c2127',
            },
            info: {
              background: '#93c5fd',
              hoverBackground: '#7eb8fc',
              activeBackground: '#69abfb',
              borderColor: '#93c5fd',
              color: '#1c2127',
            },
            warn: {
              background: '#fcd34d',
              hoverBackground: '#fbc93e',
              activeBackground: '#fabf2e',
              borderColor: '#fcd34d',
              color: '#1c2127',
            },
            help: {
              background: '#d8b4fe',
              hoverBackground: '#d1a5fd',
              activeBackground: '#ca96fd',
              borderColor: '#d8b4fe',
              color: '#1c2127',
            },
            danger: {
              background: '#fca5a5',
              hoverBackground: '#fb9393',
              activeBackground: '#fa8181',
              borderColor: '#fca5a5',
              color: '#1c2127',
            },
          },
          text: {
            plain: {
              hoverBackground: '{content.hoverBackground}',
              activeBackground: 'rgba(255, 255, 255, 0.16)',
              color: '{text.mutedColor}',
            },
          },
        },
      },
      root: {
        borderRadius: '{borderRadius.md}',
        paddingX: '1.25rem', // $buttonPadding: 0.75rem 1.25rem
        paddingY: '0.75rem',
        transitionDuration: '{transitionDuration}',
        sm: {
          fontSize: '0.875rem', // $scaleSM: 0.875
          paddingX: '1rem',
          paddingY: '0.625rem',
        },
        lg: {
          fontSize: '1.25rem', // $scaleLG: 1.25
          paddingX: '1.5rem',
          paddingY: '0.875rem',
        },
      },
    },

    // ===== DATATABLE COMPONENT =====
    // Source: theme-base/components/data/_datatable.scss
    datatable: {
      root: {
        borderColor: '{content.borderColor}',
        transitionDuration: '{transitionDuration}',
      },
      header: {
        background: '{surface.100}',
        borderColor: '{surface.300}',
        borderWidth: '1px 0',
        color: '{surface.800}',
        padding: '1rem', // $tableHeaderPadding
      },
      headerCell: {
        background: '{surface.100}', // $tableHeaderCellBg
        hoverBackground: '{surface.200}', // $tableHeaderCellHoverBg
        selectedBackground: '{highlight.background}',
        borderColor: '{surface.300}', // $tableHeaderCellBorder
        color: '{surface.800}', // $tableHeaderCellTextColor
        hoverColor: '{surface.800}',
        selectedColor: '{highlight.color}',
        gap: '0.5rem',
        padding: '1rem', // $tableHeaderCellPadding
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: 'inset 0 0 0 0.15rem {primary.100}',
        },
      },
      columnTitle: {
        fontWeight: '700',
      },
      sortIcon: {
        color: '{surface.800}', // $tableHeaderCellIconColor
        hoverColor: '{surface.800}',
      },
      row: {
        background: '{surface.0}', // $tableBodyRowBg
        hoverBackground: '{surface.200}', // $tableBodyRowHoverBg
        selectedBackground: '{highlight.background}',
        color: '{surface.700}', // $tableBodyRowTextColor
        hoverColor: '{surface.900}', // $tableBodyRowTextHoverColor
        selectedColor: '{highlight.color}',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: 'inset 0 0 0 0.15rem {primary.100}',
        },
      },
      bodyCell: {
        borderColor: '{surface.300}', // $tableBodyCellBorder
        padding: '1rem', // $tableBodyCellPadding
      },
      footerCell: {
        background: '{surface.100}', // $tableFooterCellBg
        borderColor: '{surface.300}',
        color: '{surface.800}', // $tableFooterCellTextColor
        padding: '1rem', // $tableFooterCellPadding
      },
      columnFooter: {
        fontWeight: '700',
      },
      footer: {
        background: '{surface.100}', // $tableFooterBg
        borderColor: '{surface.300}',
        borderWidth: '0 0 1px 0',
        color: '{surface.800}',
        padding: '1rem',
      },
      resizeIndicator: {
        color: '{primary.color}', // $tableResizerHelperBg
      },
      colorScheme: {
        light: {
          root: {
            borderColor: '{surface.300}',
          },
          row: {
            stripedBackground: '{surface.50}', // $tableBodyRowEvenBg
          },
          bodyCell: {
            selectedBorderColor: '{primary.color}',
          },
        },
        dark: {
          root: {
            borderColor: '{content.borderColor}',
          },
          header: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.mutedColor}', // Dark theme headers use muted color
          },
          headerCell: {
            background: '{content.background}',
            hoverBackground: '{content.hoverBackground}',
            selectedBackground: '{highlight.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            selectedColor: '{highlight.color}',
          },
          sortIcon: {
            color: '{text.mutedColor}',
            hoverColor: '{text.color}',
          },
          row: {
            background: '{content.background}',
            hoverBackground: '{content.hoverBackground}',
            selectedBackground: '{highlight.background}',
            color: '{text.color}',
            selectedColor: '{highlight.color}',
            stripedBackground: '#1f2937', // $tableBodyRowEvenBg (specific color)
          },
          bodyCell: {
            borderColor: '{content.borderColor}',
            selectedBorderColor: '{primary.color}',
          },
          footerCell: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
          },
          footer: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
          },
        },
      },
    },

    // ===== SELECT (DROPDOWN) COMPONENT =====
    // Source: theme-base/components/input/_dropdown.scss
    select: {
      root: {
        background: '{surface.0}', // $inputBg
        disabledBackground: '{surface.200}',
        filledBackground: '{surface.200}', // $inputFilledBg
        filledHoverBackground: '{surface.200}', // $inputFilledHoverBg
        filledFocusBackground: '{surface.0}', // $inputFilledFocusBg
        borderColor: '{surface.400}', // $inputBorder: 1px solid $shade400
        hoverBorderColor: '{primary.color}', // $inputHoverBorderColor
        focusBorderColor: '{primary.color}', // $inputFocusBorderColor
        invalidBorderColor: '{red.500}', // $inputErrorBorderColor
        color: '{surface.700}', // $inputTextColor
        disabledColor: '{surface.500}',
        placeholderColor: '{surface.600}', // $inputPlaceholderTextColor
        invalidPlaceholderColor: '{red.500}',
        shadow: '0 0 0 0 transparent',
        paddingX: '0.75rem', // $inputPadding
        paddingY: '0.75rem',
        borderRadius: '{borderRadius.md}',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0.2rem {primary.100}',
        },
        transitionDuration: '{transitionDuration}',
      },
      dropdown: {
        width: '3rem', // $inputGroupAddOnMinWidth
        color: '{surface.600}', // $inputIconColor
      },
      overlay: {
        background: '{surface.0}', // $inputOverlayBg
        borderColor: 'transparent', // $inputOverlayBorder: 0 none
        borderRadius: '{borderRadius.md}',
        color: '{surface.700}',
        shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)', // $inputOverlayShadow
      },
      list: {
        padding: '0.75rem 0', // $inputListPadding
        gap: '0',
        header: {
          padding: '0.75rem 1.25rem', // $inputListHeaderPadding
        },
      },
      option: {
        focusBackground: '{surface.200}', // $inputListItemFocusBg (changed from 300)
        selectedBackground: '{highlight.background}',
        selectedFocusBackground: '{highlight.focusBackground}',
        color: '{surface.700}', // $inputListItemTextColor
        focusColor: '{surface.700}',
        selectedColor: '{highlight.color}',
        selectedFocusColor: '{highlight.color}',
        padding: '0.75rem 1.25rem', // $inputListItemPadding
        borderRadius: '0', // $inputListItemBorderRadius
      },
      optionGroup: {
        background: '{surface.0}', // $submenuHeaderBg
        color: '{surface.800}', // $submenuHeaderTextColor
        fontWeight: '700', // $submenuHeaderFontWeight
        padding: '0.75rem 1.25rem', // $submenuHeaderPadding
      },
      clearIcon: {
        color: '{surface.600}',
      },
      checkmark: {
        color: '{surface.800}',
      },
      emptyMessage: {
        padding: '0.75rem 1.25rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: '{surface.400}',
            color: '{surface.700}',
            placeholderColor: '{surface.600}',
          },
          overlay: {
            background: '{surface.0}',
            borderColor: 'transparent',
            color: '{surface.700}',
          },
        },
        dark: {
          root: {
            background: '{surface.100}', // $inputBg: $shade900 (which is #393760)
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            placeholderColor: '{text.mutedColor}',
          },
          dropdown: {
            color: '{text.mutedColor}',
          },
          overlay: {
            background: '{content.background}', // $inputOverlayBg: $shade800
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)', // $inputOverlayShadow
          },
          option: {
            focusBackground: '{content.hoverBackground}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{content.borderColor}', // $inputListItemFocusBg
            color: '{text.color}',
            focusColor: '{text.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{text.color}',
          },
          list: {
            header: {
              padding: '0.75rem 1.25rem',
            },
          },
          optionGroup: {
            background: '{surface.0}', // $submenuHeaderBg: $shade700
            color: '{text.color}',
          },
        },
      },
    },

    // ===== INPUTTEXT COMPONENT =====
    // Source: theme-base/components/input/_inputtext.scss (via mixins)
    inputtext: {
      root: {
        background: '{surface.0}',
        disabledBackground: '{surface.200}',
        filledBackground: '{surface.200}',
        filledHoverBackground: '{surface.200}',
        filledFocusBackground: '{surface.0}',
        borderColor: '{surface.400}',
        hoverBorderColor: '{primary.color}',
        focusBorderColor: '{primary.color}',
        invalidBorderColor: '#fd6e6e', // $errorColor
        color: '{surface.700}',
        disabledColor: '{surface.500}',
        placeholderColor: '{surface.600}',
        invalidPlaceholderColor: '#fd6e6e',
        shadow: '0 0 0 0 transparent',
        paddingX: '0.75rem',
        paddingY: '0.75rem',
        borderRadius: '{borderRadius.md}',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0.2rem {primary.100}',
        },
        transitionDuration: '{transitionDuration}',
        sm: {
          fontSize: '0.875rem',
          paddingX: '0.625rem',
          paddingY: '0.625rem',
        },
        lg: {
          fontSize: '1.125rem',
          paddingX: '0.875rem',
          paddingY: '0.875rem',
        },
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: '{surface.400}',
            color: '{surface.700}',
            placeholderColor: '{surface.600}',
          },
        },
        dark: {
          root: {
            background: '{surface.100}', // $inputBg: $shade900
            disabledBackground: '{surface.200}',
            filledBackground: '{content.borderColor}',
            filledHoverBackground: '{content.borderColor}',
            filledFocusBackground: '{content.borderColor}',
            borderColor: '{content.borderColor}',
            hoverBorderColor: '{primary.color}',
            focusBorderColor: '{primary.color}',
            invalidBorderColor: '#fca5a5', // $errorColor in dark theme
            color: '{text.color}',
            disabledColor: '{text.mutedColor}',
            placeholderColor: '{text.mutedColor}',
            invalidPlaceholderColor: '#fca5a5',
          },
        },
      },
    },

    // ===== CHECKBOX COMPONENT =====
    // Source: theme-base/components/input/_checkbox.scss
    checkbox: {
      root: {
        borderRadius: '{borderRadius.sm}',
        width: '22px', // $checkboxWidth
        height: '22px', // $checkboxHeight
        background: '{surface.0}',
        checkedBackground: '{primary.color}', // $checkboxActiveBg
        checkedHoverBackground: '{primary.800}', // $checkboxActiveHoverBg: $primaryDarkerColor
        disabledBackground: '{surface.200}',
        filledBackground: '{surface.100}',
        borderColor: '{surface.400}', // $checkboxBorder: 2px solid $shade400
        hoverBorderColor: '{primary.color}',
        focusBorderColor: '{primary.color}',
        checkedBorderColor: '{primary.color}', // $checkboxActiveBorderColor
        checkedHoverBorderColor: '{primary.800}', // $checkboxActiveHoverBorderColor
        checkedFocusBorderColor: '{primary.color}',
        checkedDisabledBorderColor: '{surface.300}',
        invalidBorderColor: '#fd6e6e',
        shadow: '0 0 0 0 transparent',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0.2rem {primary.100}',
        },
        transitionDuration: '{transitionDuration}',
      },
      icon: {
        size: '14px', // $checkboxIconFontSize
        color: '#ffffff', // $checkboxIconActiveColor: $primaryTextColor
        checkedColor: '#ffffff',
        checkedHoverColor: '#ffffff',
        disabledColor: '{surface.500}',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            disabledBackground: '{surface.200}',
            borderColor: '{surface.400}',
            invalidBorderColor: '#fd6e6e',
          },
        },
        dark: {
          root: {
            background: '{surface.100}', // Dark theme uses elevated surface
            checkedBackground: '{primary.color}',
            checkedHoverBackground: '{primary.activeColor}', // $checkboxActiveHoverBg: $primaryLighterColor
            disabledBackground: '{surface.200}',
            borderColor: '{content.borderColor}',
            hoverBorderColor: '{primary.color}',
            focusBorderColor: '{primary.color}',
            checkedBorderColor: '{primary.color}',
            checkedHoverBorderColor: '{primary.activeColor}',
            invalidBorderColor: '#fca5a5',
          },
          icon: {
            color: '{primary.contrastColor}',
            checkedColor: '{primary.contrastColor}',
            disabledColor: '{text.mutedColor}',
          },
        },
      },
    },

    // ===== RADIOBUTTON COMPONENT =====
    // Source: theme-base/components/input/_radiobutton.scss
    radiobutton: {
      root: {
        width: '22px', // $radiobuttonWidth
        height: '22px', // $radiobuttonHeight
        background: '{surface.0}',
        checkedBackground: '{primary.color}', // $radiobuttonActiveBg
        checkedHoverBackground: '{primary.800}', // $radiobuttonActiveHoverBg
        disabledBackground: '{surface.200}',
        filledBackground: '{surface.100}',
        borderColor: '{surface.400}', // $radiobuttonBorder: 2px solid $shade400
        hoverBorderColor: '{primary.color}',
        focusBorderColor: '{primary.color}',
        checkedBorderColor: '{primary.color}',
        checkedHoverBorderColor: '{primary.800}',
        checkedFocusBorderColor: '{primary.color}',
        checkedDisabledBorderColor: '{surface.300}',
        invalidBorderColor: '#fd6e6e',
        shadow: '0 0 0 0 transparent',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0.2rem {primary.100}',
        },
        transitionDuration: '{transitionDuration}',
      },
      icon: {
        size: '12px', // $radiobuttonIconSize
        checkedColor: '#ffffff', // $radiobuttonIconActiveColor
        checkedHoverColor: '#ffffff',
        disabledColor: '{surface.500}',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            disabledBackground: '{surface.200}',
            borderColor: '{surface.400}',
            invalidBorderColor: '#fd6e6e',
          },
        },
        dark: {
          root: {
            background: '{surface.100}',
            checkedBackground: '{primary.color}',
            checkedHoverBackground: '{primary.activeColor}',
            disabledBackground: '{surface.200}',
            borderColor: '{content.borderColor}',
            hoverBorderColor: '{primary.color}',
            focusBorderColor: '{primary.color}',
            checkedBorderColor: '{primary.color}',
            checkedHoverBorderColor: '{primary.activeColor}',
            invalidBorderColor: '#fca5a5',
          },
          icon: {
            checkedColor: '{primary.contrastColor}',
            disabledColor: '{text.mutedColor}',
          },
        },
      },
    },

    // ===== INPUTSWITCH COMPONENT =====
    // Source: theme-base/components/input/_inputswitch.scss
    inputswitch: {
      root: {
        width: '3em', // $inputSwitchWidth
        height: '1.7em', // $inputSwitchHeight
        borderRadius: '2.143em', // $inputSwitchBorderRadius
        gap: '0',
        shadow: '0 0 0 0 transparent',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0.2rem {primary.100}',
        },
        borderWidth: '0',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        checkedBorderColor: 'transparent',
        checkedHoverBorderColor: 'transparent',
        invalidBorderColor: 'transparent',
        transitionDuration: '{transitionDuration}',
        slideDuration: '0.2s',
      },
      slider: {
        background: '{surface.300}', // $inputSwitchSliderOffBg
        hoverBackground: '{surface.400}', // $inputSwitchSliderOffHoverBg
        checkedBackground: '{primary.color}', // $inputSwitchSliderOnBg
        checkedHoverBackground: '{primary.600}', // $inputSwitchSliderOnHoverBg
        borderRadius: '2.143em',
      },
      handle: {
        borderRadius: '50%', // $inputSwitchHandleBorderRadius
        size: '1.25em', // $inputSwitchHandleWidth/Height
        background: '{surface.100}', // $inputSwitchHandleOffBg
        hoverBackground: '{surface.100}',
        checkedBackground: '#ffffff', // $inputSwitchHandleOnBg: $primaryTextColor
        checkedHoverBackground: '#ffffff',
        color: '{text.mutedColor}',
        hoverColor: '{text.color}',
        checkedColor: '{primary.contrastColor}',
        checkedHoverColor: '{primary.contrastColor}',
      },
      colorScheme: {
        light: {
          slider: {
            background: '{surface.300}',
            hoverBackground: '{surface.400}',
            checkedBackground: '{primary.color}',
            checkedHoverBackground: '{primary.600}',
          },
          handle: {
            background: '{surface.100}',
            checkedBackground: '#ffffff',
          },
        },
        dark: {
          slider: {
            background: '{content.borderColor}', // $inputSwitchSliderOffBg: $shade600
            hoverBackground: '{surface.0}', // $inputSwitchSliderOffHoverBg: $shade700
            checkedBackground: '{primary.color}',
            checkedHoverBackground: '{primary.hoverColor}', // $inputSwitchSliderOnHoverBg
          },
          handle: {
            background: '{text.mutedColor}', // $inputSwitchHandleOffBg: $shade100
            checkedBackground: '{primary.activeColor}', // $inputSwitchHandleOnBg: $primaryLighterColor
          },
        },
      },
    },

    // ===== PANEL COMPONENT =====
    // Source: theme-base/components/panel/_panel.scss
    panel: {
      root: {
        background: '{surface.0}',
        borderColor: '{content.borderColor}',
        borderRadius: '{borderRadius.md}',
        color: '{surface.700}',
      },
      header: {
        background: '{surface.100}', // $panelHeaderBg
        borderColor: '{surface.300}', // $panelHeaderBorder
        borderWidth: '1px',
        borderRadius: '{borderRadius.md}',
        color: '{surface.800}', // $panelHeaderTextColor
        padding: '1.25rem', // $panelHeaderPadding
      },
      toggleableHeader: {
        padding: '0.75rem 1.25rem', // $panelToggleableHeaderPadding
      },
      title: {
        fontWeight: '700',
      },
      content: {
        padding: '1rem', // $panelContentPadding
      },
      footer: {
        padding: '0.75rem 1.25rem', // $panelFooterPadding
      },
      colorScheme: {
        light: {
          header: {
            background: '{surface.100}',
            borderColor: '{surface.300}',
            color: '{surface.800}',
          },
        },
        dark: {
          root: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
          },
          header: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
          },
        },
      },
    },

    // ===== ACCORDION COMPONENT =====
    // Source: theme-base/components/panel/_accordion.scss
    accordion: {
      root: {
        transitionDuration: '{transitionDuration}',
      },
      panel: {
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
      },
      header: {
        color: '{text.mutedColor}', // $accordionHeaderTextColor: $shade600
        hoverColor: '{text.color}',
        activeColor: '{text.color}',
        padding: '1.25rem', // $accordionHeaderPadding
        fontWeight: '700', // $accordionHeaderFontWeight
        borderRadius: '0',
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        background: '{surface.100}', // $accordionHeaderBg
        hoverBackground: '{surface.200}', // $accordionHeaderHoverBg
        activeBackground: '{surface.100}', // $accordionHeaderActiveBg
        activeHoverBackground: '{surface.200}', // $accordionHeaderActiveHoverBg
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: 'inset 0 0 0 0.15rem {primary.100}',
        },
        toggleIcon: {
          color: '{text.mutedColor}',
          hoverColor: '{text.color}',
          activeColor: '{text.color}',
          activeHoverColor: '{text.color}',
        },
        first: {
          topBorderRadius: '0',
        },
        last: {
          bottomBorderRadius: '0',
          activeBottomBorderRadius: '0',
        },
      },
      content: {
        borderWidth: '1px',
        borderColor: '{content.borderColor}',
        background: '{surface.0}', // $accordionContentBg
        color: '{text.color}', // $accordionContentTextColor
        padding: '1rem', // $accordionContentPadding
      },
      colorScheme: {
        light: {
          panel: {
            borderColor: '{surface.300}',
          },
          header: {
            background: '{surface.100}',
            hoverBackground: '{surface.200}',
            activeBackground: '{surface.100}',
            activeHoverBackground: '{surface.200}',
            borderColor: '{surface.300}',
            color: '{surface.600}',
            hoverColor: '{surface.800}',
            activeColor: '{surface.800}',
            activeHoverColor: '{surface.800}',
          },
          content: {
            background: '{surface.0}',
            borderColor: '{surface.300}',
            color: '{surface.700}',
          },
        },
        dark: {
          panel: {
            borderColor: '{content.borderColor}',
          },
          header: {
            background: '{content.background}',
            hoverBackground: '{content.hoverBackground}',
            activeBackground: '{content.background}',
            activeHoverBackground: '{content.hoverBackground}',
            borderColor: '{content.borderColor}',
            color: '{text.mutedColor}',
            hoverColor: '{text.color}',
            activeColor: '{text.color}',
            activeHoverColor: '{text.color}',
          },
          content: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
          },
        },
      },
    },

    // ===== TABVIEW COMPONENT =====
    // Source: theme-base/components/panel/_tabview.scss
    tabview: {
      root: {
        transitionDuration: '{transitionDuration}',
      },
      tabList: {
        background: '{surface.0}', // $tabviewNavBg
        borderColor: '{surface.300}', // $tabviewNavBorder
      },
      navButton: {
        background: '{surface.0}',
        color: '{surface.600}',
        hoverColor: '{surface.800}',
        shadow: 'none',
      },
      tab: {
        borderColor: '{surface.300}', // $tabviewHeaderBorderColor (bottom part)
        activeBorderColor: '{primary.color}', // $tabviewHeaderActiveBorderColor
        color: '{surface.600}', // $tabviewHeaderTextColor
        hoverColor: '{surface.600}', // $tabviewHeaderTextHoverColor
        activeColor: '{primary.color}', // $tabviewHeaderTextActiveColor
      },
      tabPanel: {
        background: '{surface.0}', // $tabviewContentBg
        color: '{surface.700}', // $tabviewContentTextColor
      },
      colorScheme: {
        light: {
          tabList: {
            background: '{surface.0}',
            borderColor: '{surface.300}',
          },
          tab: {
            borderColor: '{surface.300}',
            activeBorderColor: '{primary.color}',
            color: '{surface.600}',
            hoverColor: '{surface.600}',
            activeColor: '{primary.color}',
          },
          tabPanel: {
            background: '{surface.0}',
            color: '{surface.700}',
          },
        },
        dark: {
          tabList: {
            background: 'transparent', // $tabviewNavBg: transparent
            borderColor: '{content.borderColor}',
          },
          navButton: {
            background: 'transparent',
            color: '{text.mutedColor}',
            hoverColor: '{text.color}',
          },
          tab: {
            borderColor: '{content.borderColor}',
            activeBorderColor: '{primary.color}',
            color: '{text.mutedColor}', // $tabviewHeaderTextColor: $shade100
            hoverColor: '{text.color}', // $tabviewHeaderTextHoverColor: $shade000
            activeColor: '{primary.color}', // $tabviewHeaderTextActiveColor
          },
          tabPanel: {
            background: '{content.background}', // $tabviewContentBg
            color: '{text.color}', // $tabviewContentTextColor
          },
        },
      },
    },

    // ===== DIALOG COMPONENT =====
    // Source: theme-base/components/overlay/_dialog.scss
    dialog: {
      root: {
        background: '{surface.0}', // $dialogHeaderBg
        borderColor: 'transparent', // $dialogHeaderBorder: 0 none
        borderRadius: '{borderRadius.lg}',
        color: '{surface.700}',
        shadow: '0 2px 12px rgba(0, 0, 0, 0.1)', // $overlayContainerShadow (simplified)
      },
      header: {
        padding: '1.5rem', // $dialogHeaderPadding
        gap: '0.5rem',
      },
      title: {
        fontWeight: '700',
        fontSize: '1.25rem',
      },
      content: {
        padding: '0 1.5rem 2rem 1.5rem', // $dialogContentPadding
      },
      footer: {
        padding: '0 1.5rem 1.5rem 1.5rem', // $dialogFooterPadding
        gap: '0.5rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '{surface.700}',
          },
        },
        dark: {
          root: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            shadow:
              '0 11px 15px -7px rgba(0,0,0,0.2), 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12)', // $overlayContainerShadow
          },
        },
      },
    },

    // ===== TOAST COMPONENT =====
    // Source: theme-base/components/messages/_toast.scss
    toast: {
      root: {
        width: '25rem',
        borderRadius: '{borderRadius.md}',
        borderWidth: '0 0 0 6px', // $toastBorderWidth
        transitionDuration: '{transitionDuration}',
      },
      icon: {
        size: '2rem', // $toastIconFontSize
      },
      content: {
        padding: '1rem', // $toastPadding
        gap: '1rem', // $toastMessageTextMargin: 0 0 0 1rem
      },
      text: {
        gap: '0.5rem', // $inlineSpacing
      },
      summary: {
        fontWeight: '700', // $toastTitleFontWeight
        fontSize: '1rem',
      },
      detail: {
        fontWeight: '400',
        fontSize: '1rem',
      },
      closeButton: {
        width: '2rem',
        height: '2rem',
        borderRadius: '50%',
        focusRing: {
          width: '0',
          style: 'none',
          offset: '0',
        },
      },
      closeIcon: {
        size: '1rem',
      },
      colorScheme: {
        light: {
          info: {
            background: 'rgba(219, 234, 254, 0.7)', // $infoMessageBg
            borderColor: '#3b82f6', // $infoMessageBorder
            color: '#3b82f6', // $infoMessageTextColor
            detailColor: '#3b82f6', // $infoMessageIconColor
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)', // $toastShadow
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '{primary.100}',
                shadow: '0 0 0 0.2rem {primary.100}',
              },
            },
          },
          success: {
            background: 'rgba(228, 248, 240, 0.7)', // $successMessageBg
            borderColor: '#1ea97c', // $successMessageBorder
            color: '#1ea97c', // $successMessageTextColor
            detailColor: '#1ea97c',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '#bbf7d0',
                shadow: '0 0 0 0.2rem #bbf7d0',
              },
            },
          },
          warn: {
            background: 'rgba(255, 242, 226, 0.7)', // $warningMessageBg
            borderColor: '#cc8925', // $warningMessageBorder
            color: '#cc8925', // $warningMessageTextColor
            detailColor: '#cc8925',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '#fde68a',
                shadow: '0 0 0 0.2rem #fde68a',
              },
            },
          },
          error: {
            background: 'rgba(255, 231, 230, 0.7)', // $errorMessageBg
            borderColor: '#ff5757', // $errorMessageBorder
            color: '#ff5757', // $errorMessageTextColor
            detailColor: '#ff5757',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '#fecaca',
                shadow: '0 0 0 0.2rem #fecaca',
              },
            },
          },
          secondary: {
            background: '{surface.100}',
            borderColor: '{surface.400}',
            color: '{surface.700}',
            detailColor: '{surface.600}',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
            closeButton: {
              hoverBackground: '{surface.200}',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
          contrast: {
            background: '{surface.900}',
            borderColor: '{surface.950}',
            color: '{surface.50}',
            detailColor: '{surface.0}',
            shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.1)',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
        },
        dark: {
          info: {
            background: 'rgba(59, 130, 246, 0.2)', // $infoMessageBg (dark theme)
            borderColor: '#3b82f6',
            color: '#93c5fd', // $infoMessageTextColor (blue-300)
            detailColor: '#93c5fd',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)', // $toastShadow (none in original)
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#bfdbfe',
                shadow: '0 0 0 0.2rem #bfdbfe',
              },
            },
          },
          success: {
            background: 'rgba(16, 185, 129, 0.2)', // $successMessageBg
            borderColor: '#10b981',
            color: '#6ee7b7', // $successMessageTextColor
            detailColor: '#6ee7b7',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#bbf7d0',
                shadow: '0 0 0 0.2rem #bbf7d0',
              },
            },
          },
          warn: {
            background: 'rgba(234, 179, 8, 0.2)', // $warningMessageBg
            borderColor: '#eab308',
            color: '#fde047', // $warningMessageTextColor
            detailColor: '#fde047',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#fde68a',
                shadow: '0 0 0 0.2rem #fde68a',
              },
            },
          },
          error: {
            background: 'rgba(239, 68, 68, 0.2)', // $errorMessageBg
            borderColor: '#ef4444',
            color: '#fca5a5', // $errorMessageTextColor
            detailColor: '#fca5a5',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#fecaca',
                shadow: '0 0 0 0.2rem #fecaca',
              },
            },
          },
          secondary: {
            background: '{surface.200}',
            borderColor: '{surface.400}',
            color: '{surface.700}',
            detailColor: '{surface.600}',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
            closeButton: {
              hoverBackground: '{surface.300}',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
          contrast: {
            background: '{surface.900}',
            borderColor: '{surface.950}',
            color: '{surface.50}',
            detailColor: '{surface.0}',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
        },
      },
    },

    // ===== MESSAGE COMPONENT =====
    // Source: theme-base/components/messages/_message.scss
    message: {
      root: {
        borderRadius: '{borderRadius.md}',
        borderWidth: '0 0 0 6px', // $messageBorderWidth
        transitionDuration: '{transitionDuration}',
      },
      content: {
        padding: '1.25rem 1.75rem', // $messagePadding
        gap: '0.5rem', // $inlineSpacing
      },
      text: {
        fontSize: '1rem', // $messageTextFontSize
        fontWeight: '500', // $messageTextFontWeight
      },
      icon: {
        size: '1.5rem', // $messageIconFontSize
      },
      closeButton: {
        width: '2rem',
        height: '2rem',
        borderRadius: '50%',
        focusRing: {
          width: '0',
          style: 'none',
          offset: '0',
        },
      },
      closeIcon: {
        size: '1rem',
      },
      colorScheme: {
        light: {
          // Same colors as toast
          info: {
            background: 'rgba(219, 234, 254, 0.7)',
            borderColor: '#3b82f6',
            color: '#3b82f6',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '{primary.100}',
                shadow: '0 0 0 0.2rem {primary.100}',
              },
            },
          },
          success: {
            background: 'rgba(228, 248, 240, 0.7)',
            borderColor: '#1ea97c',
            color: '#1ea97c',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '#bbf7d0',
                shadow: '0 0 0 0.2rem #bbf7d0',
              },
            },
          },
          warn: {
            background: 'rgba(255, 242, 226, 0.7)',
            borderColor: '#cc8925',
            color: '#cc8925',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '#fde68a',
                shadow: '0 0 0 0.2rem #fde68a',
              },
            },
          },
          error: {
            background: 'rgba(255, 231, 230, 0.7)',
            borderColor: '#ff5757',
            color: '#ff5757',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.3)',
              focusRing: {
                color: '#fecaca',
                shadow: '0 0 0 0.2rem #fecaca',
              },
            },
          },
          secondary: {
            background: '{surface.100}',
            borderColor: '{surface.400}',
            color: '{surface.700}',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: '{surface.200}',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
          contrast: {
            background: '{surface.900}',
            borderColor: '{surface.950}',
            color: '{surface.50}',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: '{surface.800}',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
        },
        dark: {
          info: {
            background: 'rgba(59, 130, 246, 0.16)',
            borderColor: '#3b82f6',
            color: '#93c5fd',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#bfdbfe',
                shadow: '0 0 0 0.2rem #bfdbfe',
              },
            },
          },
          success: {
            background: 'rgba(30, 169, 124, 0.16)',
            borderColor: '#1ea97c',
            color: '#6ee7b7',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#bbf7d0',
                shadow: '0 0 0 0.2rem #bbf7d0',
              },
            },
          },
          warn: {
            background: 'rgba(204, 137, 37, 0.16)',
            borderColor: '#cc8925',
            color: '#fcd34d',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#fde68a',
                shadow: '0 0 0 0.2rem #fde68a',
              },
            },
          },
          error: {
            background: 'rgba(255, 87, 87, 0.16)',
            borderColor: '#ff5757',
            color: '#fca5a5',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: 'rgba(255, 255, 255, 0.05)',
              focusRing: {
                color: '#fecaca',
                shadow: '0 0 0 0.2rem #fecaca',
              },
            },
          },
          secondary: {
            background: '{surface.200}',
            borderColor: '{surface.400}',
            color: '{surface.700}',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: '{surface.300}',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
          contrast: {
            background: '{surface.0}',
            borderColor: '{surface.100}',
            color: '{surface.950}',
            shadow: '0 0 0 0 transparent',
            closeButton: {
              hoverBackground: '{surface.100}',
              focusRing: {
                color: '{surface.400}',
                shadow: '0 0 0 0.2rem {surface.400}',
              },
            },
          },
        },
      },
    },

    // ===== CARD COMPONENT =====
    // Source: theme-base/components/panel/_card.scss
    card: {
      root: {
        background: '{surface.0}',
        borderRadius: '{borderRadius.md}',
        color: '{surface.700}',
        shadow: '0px 4px 30px rgba(221, 224, 255, 0.54)', // $cardShadow
      },
      body: {
        padding: '1.25rem', // $cardBodyPadding
        gap: '0.5rem',
      },
      caption: {
        gap: '0.5rem',
      },
      title: {
        fontSize: '1.5rem', // $cardTitleFontSize
        fontWeight: '700', // $cardTitleFontWeight
      },
      subtitle: {
        color: '{text.mutedColor}', // $cardSubTitleColor: $shade600
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '{surface.700}',
          },
          subtitle: {
            color: '{surface.600}',
          },
        },
        dark: {
          root: {
            background: '{content.background}',
            color: '{text.color}',
          },
          subtitle: {
            color: '{text.mutedColor}',
          },
        },
      },
    },

    // ===== PANELMENU COMPONENT =====
    // Source: theme-base/components/menu/_panelmenu.scss
    panelmenu: {
      root: {
        gap: '0',
        transitionDuration: '{transitionDuration}',
      },
      panel: {
        borderWidth: '0',
        borderColor: 'transparent',
        background: 'transparent',
        borderRadius: '0',
        padding: '0',
        first: {
          borderWidth: '0',
          topBorderRadius: '0',
        },
        last: {
          borderWidth: '0',
          bottomBorderRadius: '0',
        },
      },
      item: {
        focusBackground: '{content.hoverBackground}',
        color: '{text.color}',
        focusColor: '{text.color}',
        gap: '0.5rem',
        padding: '0.75rem 1.25rem',
        borderRadius: '0',
        icon: {
          color: '{text.mutedColor}',
          focusColor: '{text.color}',
        },
      },
      submenu: {
        indent: '1.25rem',
      },
      submenuIcon: {
        color: '{text.mutedColor}',
        focusColor: '{text.color}',
      },
    },

    // ===== POPOVER COMPONENT =====
    // Source: PrimeNG 19 new component (replaces OverlayPanel from v17)
    // Based on theme-base/components/overlay/_overlaypanel.scss
    popover: {
      root: {
        background: '{surface.0}',
        borderColor: '{content.borderColor}',
        color: '{text.color}',
        borderRadius: '{borderRadius.md}',
        shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
        gutter: '10px',
        arrowOffset: '1.25rem',
      },
      content: {
        padding: '1rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: 'transparent',
            color: '{surface.700}',
          },
        },
        dark: {
          root: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
          },
          content: {
            padding: '1rem',
          },
        },
      },
    },

    // ===== MENU COMPONENT =====
    // Source: theme-base/components/menu/_menu.scss
    menu: {
      root: {
        background: '{surface.0}', // $menuBg
        borderColor: '{surface.300}', // $menuBorder
        borderRadius: '{borderRadius.md}',
        color: '{surface.700}', // $menuTextColor
        shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)', // $overlayMenuShadow
        transitionDuration: '{transitionDuration}',
      },
      list: {
        padding: '0.25rem 0', // $verticalMenuPadding
        gap: '0',
      },
      item: {
        focusBackground: '{surface.200}', // $menuitemHoverBg
        color: '{surface.700}', // $menuitemTextColor
        focusColor: '{surface.700}', // $menuitemTextFocusColor
        padding: '0.75rem 1.25rem', // $menuitemPadding
        borderRadius: '0', // $menuitemBorderRadius
        gap: '0.5rem',
        icon: {
          color: '{surface.600}', // $menuitemIconColor
          focusColor: '{surface.700}', // $menuitemIconFocusColor
        },
      },
      submenuLabel: {
        background: '{surface.0}', // $submenuHeaderBg
        color: '{surface.800}', // $submenuHeaderTextColor
        fontWeight: '700', // $submenuHeaderFontWeight
        padding: '0.75rem 1.25rem', // $submenuHeaderPadding
      },
      separator: {
        borderColor: '{surface.300}',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: '{surface.300}',
            color: '{surface.700}',
          },
        },
        dark: {
          root: {
            background: '{surface.0}', // $menuBg: $shade700 (#242247)
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)', // $overlayMenuShadow
          },
          item: {
            focusBackground: '{content.hoverBackground}', // $menuitemHoverBg: $hoverBg
            color: '{text.color}',
            focusColor: '{text.color}',
            icon: {
              color: '{text.mutedColor}',
              focusColor: '{text.color}',
            },
          },
          submenuLabel: {
            background: '{surface.0}', // $submenuHeaderBg: $shade700
            color: '{text.color}',
          },
        },
      },
    },

    // ===== PAGINATOR COMPONENT =====
    // Source: theme-base/components/data/_paginator.scss
    paginator: {
      root: {
        background: '{surface.0}', // $paginatorBg
        color: '{surface.600}', // $paginatorTextColor
        padding: '0.5rem 1rem', // $paginatorPadding
        borderRadius: '0',
        gap: '0.143rem', // $paginatorElementMargin (repurposed)
      },
      navButton: {
        background: 'transparent', // $paginatorElementBg
        hoverBackground: '{surface.200}', // $paginatorElementHoverBg
        selectedBackground: '{primary.color}',
        color: '{surface.600}', // $paginatorElementIconColor
        hoverColor: '{surface.800}', // $paginatorElementIconHoverColor
        selectedColor: '#ffffff',
        width: '3rem', // $paginatorElementWidth: $buttonIconOnlyWidth
        height: '3rem', // $paginatorElementHeight
        borderRadius: '50%', // $paginatorElementBorderRadius
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: 'inset 0 0 0 0.15rem {primary.100}',
        },
      },
      currentPageReport: {
        color: '{surface.700}',
      },
      jumpToPageInput: {
        maxWidth: '3rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            background: '{content.background}', // $paginatorBg: $shade800
            color: '{text.mutedColor}', // $paginatorTextColor: $shade100
          },
          navButton: {
            background: 'transparent',
            hoverBackground: '{content.hoverBackground}',
            selectedBackground: '{primary.color}',
            color: '{text.mutedColor}',
            hoverColor: '{text.color}',
            selectedColor: '{primary.contrastColor}',
            borderRadius: '{borderRadius.md}', // Dark theme uses md radius instead of circle
          },
          currentPageReport: {
            color: '{text.color}',
          },
        },
      },
    },

    // ===== CALENDAR COMPONENT =====
    // Source: theme-base/components/input/_calendar.scss
    calendar: {
      root: {
        transitionDuration: '{transitionDuration}',
      },
      panel: {
        background: '{surface.0}', // $calendarBg
        borderColor: '{surface.400}', // $calendarBorder
        borderRadius: '{borderRadius.md}',
        color: '{surface.700}', // $calendarTextColor
        padding: '0.5rem', // $calendarPadding
        shadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
      },
      header: {
        background: '{surface.0}', // $calendarHeaderBg
        borderColor: '{surface.300}', // $calendarHeaderBorder
        color: '{surface.700}', // $calendarHeaderTextColor
        padding: '0.5rem', // $calendarHeaderPadding
        fontWeight: '600', // $calendarHeaderFontWeight
        gap: '0.5rem',
      },
      title: {
        gap: '0.5rem',
        fontWeight: '600',
      },
      dropdown: {
        width: '2.5rem',
        background: 'transparent',
        color: '{surface.700}',
        hoverColor: '{primary.color}', // $calendarMonthYearHeaderHoverTextColor
        activeColor: '{surface.700}',
        borderRadius: '{borderRadius.md}',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: 'inset 0 0 0 0.15rem {primary.100}',
        },
      },
      inputIcon: {
        color: '{surface.600}',
      },
      selectMonth: {
        hoverBackground: '{surface.200}',
        color: '{surface.700}',
        hoverColor: '{surface.700}',
        padding: '0.5rem 0.75rem',
        borderRadius: '{borderRadius.md}',
      },
      selectYear: {
        hoverBackground: '{surface.200}',
        color: '{surface.700}',
        hoverColor: '{surface.700}',
        padding: '0.5rem 0.75rem',
        borderRadius: '{borderRadius.md}',
      },
      group: {
        borderColor: '{surface.300}',
        gap: '0.5rem',
      },
      dayView: {
        margin: '0.5rem 0', // $calendarTableMargin
      },
      weekDay: {
        padding: '0.5rem', // $calendarHeaderCellPadding
        fontWeight: '600',
        color: '{surface.700}',
      },
      date: {
        width: '2.5rem', // $calendarCellDateWidth
        height: '2.5rem', // $calendarCellDateHeight
        borderRadius: '50%', // $calendarCellDateBorderRadius
        padding: '0.5rem', // $calendarCellDatePadding
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: 'inset 0 0 0 0.15rem {primary.100}',
        },
      },
      dateSelected: {
        background: '{primary.color}',
        color: '{primary.contrastColor}',
      },
      dateHover: {
        background: '{surface.200}', // $calendarCellDateHoverBg
      },
      dateToday: {
        background: 'transparent', // $calendarCellDateTodayBg (dark: transparent)
        color: '{primary.color}', // $calendarCellDateTodayTextColor (dark)
      },
      monthView: {
        margin: '0.5rem 0',
      },
      month: {
        borderRadius: '{borderRadius.md}',
        padding: '0.5rem 0.75rem',
      },
      yearView: {
        margin: '0.5rem 0',
      },
      year: {
        borderRadius: '{borderRadius.md}',
        padding: '0.5rem 0.75rem',
      },
      buttonbar: {
        padding: '1rem 0', // $calendarButtonBarPadding
        borderColor: '{surface.300}',
      },
      timePicker: {
        padding: '0.5rem', // $calendarTimePickerPadding
        borderColor: '{surface.300}',
        gap: '0.5rem',
        buttonGap: '0.5rem',
      },
      colorScheme: {
        light: {
          panel: {
            background: '{surface.0}',
            borderColor: 'transparent', // $calendarOverlayBorder: 0 none
          },
          date: {
            hoverBackground: '{surface.200}',
            selectedBackground: '{primary.color}',
            selectedColor: '#ffffff',
          },
          dateToday: {
            background: '{surface.400}', // $calendarCellDateTodayBg
            color: '{surface.700}', // $calendarCellDateTodayTextColor
          },
        },
        dark: {
          panel: {
            background: '{content.background}', // $calendarBg: $shade800
            borderColor: '{content.borderColor}',
            color: '{text.color}',
            shadow:
              '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
          },
          header: {
            background: '{content.background}',
            borderColor: '{content.borderColor}',
            color: '{text.color}',
          },
          dropdown: {
            color: '{text.color}',
            hoverColor: '{primary.color}',
            activeColor: '{text.color}',
          },
          inputIcon: {
            color: '{text.mutedColor}',
          },
          selectMonth: {
            hoverBackground: '{content.hoverBackground}',
            color: '{text.color}',
            hoverColor: '{text.color}',
          },
          selectYear: {
            hoverBackground: '{content.hoverBackground}',
            color: '{text.color}',
            hoverColor: '{text.color}',
          },
          group: {
            borderColor: '{content.borderColor}',
          },
          weekDay: {
            color: '{text.color}',
          },
          date: {
            hoverBackground: '{content.hoverBackground}',
            selectedBackground: '{primary.color}',
            selectedColor: '{primary.contrastColor}',
          },
          dateToday: {
            background: 'transparent',
            color: '{primary.color}',
          },
          buttonbar: {
            borderColor: '{content.borderColor}',
          },
          timePicker: {
            borderColor: '{content.borderColor}',
          },
        },
      },
    },
  },
});
